/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-06
 */

import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeTokens, GeneratedColorPalettes } from '../../types/theme';
import {
  createMUIThemeFromTokens,
  generateCSSVariables,
  applyCSSVariables,
} from '../../utils/muiThemeAdapter';

/**
 * Props for the DynamicThemeProvider component
 */
interface DynamicThemeProviderProps {
  /**
   * Theme tokens to generate MUI theme from
   */
  tokens: ThemeTokens;
  /**
   * Generated color palettes for enhanced theme mapping
   */
  palettes?: GeneratedColorPalettes | null;
  /**
   * Whether to apply CSS variables to document root
   */
  applyCSSVars?: boolean;
  /**
   * Whether to apply CssBaseline for consistent styling
   */
  resetCSS?: boolean;
  /**
   * Children components to render with the theme
   */
  children: React.ReactNode;
}

/**
 * Dynamic theme provider that converts theme tokens to MUI theme
 * and applies it in real-time to child components
 */
const DynamicThemeProvider: React.FC<DynamicThemeProviderProps> = ({
  tokens,
  palettes = null,
  applyCSSVars = true,
  resetCSS = false,
  children,
}) => {
  // Generate MUI theme from tokens
  const muiTheme = React.useMemo(
    () => createMUIThemeFromTokens(tokens, palettes),
    [tokens, palettes]
  );

  // Apply CSS variables when tokens or palettes change
  React.useEffect(() => {
    if (applyCSSVars) {
      const cssVariables = generateCSSVariables(tokens, palettes);
      applyCSSVariables(cssVariables);
    }
  }, [tokens, palettes, applyCSSVars]);

  return (
    <ThemeProvider theme={muiTheme}>
      {resetCSS && <CssBaseline />}
      {children}
    </ThemeProvider>
  );
};

export default DynamicThemeProvider;
